import { Link } from "react-router-dom";
import { ArrowLeft, Smartphone, Monitor, Apple, Link2, RefreshCw, Info, Zap } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";

const CONTENT = {
  fr: {
    title: "Synchroniser le calendrier avec votre agenda",
    subtitle: "Suivez ces étapes pour voir les événements de votre association directement dans Google Agenda ou Apple Calendrier. Une fois abonné, les nouveaux événements s'ajoutent automatiquement.",
    autoTitle: "Le plus simple : la synchronisation automatique",
    autoText: "Dans le menu « Synchroniser » du calendrier, touchez « Connecter mon agenda Google » et autorisez SHIALOC. Les événements s'ajoutent alors automatiquement dans votre agenda Google — visibles immédiatement sur Android et iPhone, sans aucune manipulation. Les méthodes ci-dessous restent disponibles si vous préférez un abonnement par lien.",
    back: "Retour",
    tabs: { gmobile: "Google (mobile)", gweb: "Google (ordinateur)", apple: "Apple (iPhone)", manual: "Lien manuel" },
    note: "Bon à savoir : Google actualise les agendas ajoutés par URL toutes les quelques heures (parfois jusqu'à 24 h). Les nouveaux événements publiés par votre association apparaîtront donc avec un léger délai.",
    gmobile: [
      { text: "Dans SHIALOC, ouvrez le calendrier de votre association puis touchez « Synchroniser » → « Google Calendar » et validez avec votre compte Google." },
      { text: "L'agenda est ajouté à votre compte Google — mais l'application mobile Google Agenda ne l'affiche pas automatiquement.", mock: "gweb" },
      { text: "Ouvrez l'application Google Agenda → menu ☰ → Paramètres. Sous votre compte Google, touchez le nom de votre association puis activez « Synchronisation ».", mock: "gmobile" },
      { text: "Patientez quelques minutes : les événements apparaissent dans l'application. ✅" },
    ],
    gweb: [
      { text: "Dans SHIALOC, ouvrez le calendrier puis cliquez « Synchroniser » → « Google Calendar »." },
      { text: "Google ouvre une fenêtre « Ajouter cet agenda ? » — confirmez avec votre compte Google." },
      { text: "L'agenda apparaît dans la colonne de gauche de calendar.google.com, section « Autres agendas ». Vérifiez qu'il est bien coché pour afficher les événements. ✅", mock: "gweb" },
    ],
    apple: [
      { text: "Sur iPhone ou iPad, ouvrez le calendrier SHIALOC puis touchez « Synchroniser » → « Apple Calendar »." },
      { text: "iOS affiche « S'abonner au calendrier ? » — touchez « S'abonner ».", mock: "apple" },
      { text: "Ouvrez l'application Calendrier → « Calendriers » en bas : votre association apparaît dans la section « Abonnements ». ✅" },
    ],
    manual: [
      { text: "Dans le menu « Synchroniser », touchez « Copier le lien du flux »." },
      { text: "Sur calendar.google.com (ordinateur) : cliquez le « + » à côté de « Autres agendas » → « À partir de l'URL » → collez le lien → « Ajouter l'agenda ».", mock: "manual" },
      { text: "Ce lien fonctionne aussi avec Outlook, Thunderbird et toute application compatible iCal. ✅" },
    ],
    mocks: {
      gmobileTitle: "Paramètres", gmobileSync: "Synchronisation",
      gwebOthers: "Autres agendas", gwebAdd: "+",
      appleTitle: "S'abonner au calendrier ?", appleDesc: "Les événements de l'association seront ajoutés à votre calendrier.", appleCancel: "Annuler", appleOk: "S'abonner",
      manualLabel: "À partir de l'URL", manualBtn: "Ajouter l'agenda",
      assocName: "Votre association",
    },
  },
  en: {
    title: "Sync the calendar with your agenda",
    subtitle: "Follow these steps to see your association's events directly in Google Calendar or Apple Calendar. Once subscribed, new events are added automatically.",
    autoTitle: "Easiest: automatic sync",
    autoText: "In the calendar's \"Sync\" menu, tap \"Connect my Google Calendar\" and authorize SHIALOC. Events are then added automatically to your Google Calendar — instantly visible on Android and iPhone, no manual steps. The methods below remain available if you prefer a link subscription.",
    back: "Back",
    tabs: { gmobile: "Google (mobile)", gweb: "Google (desktop)", apple: "Apple (iPhone)", manual: "Manual link" },
    note: "Good to know: Google refreshes URL-based calendars every few hours (sometimes up to 24h). New events published by your association will appear with a slight delay.",
    gmobile: [
      { text: "In SHIALOC, open your association's calendar, tap \"Sync\" → \"Google Calendar\" and confirm with your Google account." },
      { text: "The calendar is added to your Google account — but the Google Calendar mobile app does not show it automatically.", mock: "gweb" },
      { text: "Open the Google Calendar app → ☰ menu → Settings. Under your Google account, tap your association's name and enable \"Sync\".", mock: "gmobile" },
      { text: "Wait a few minutes: events appear in the app. ✅" },
    ],
    gweb: [
      { text: "In SHIALOC, open the calendar and click \"Sync\" → \"Google Calendar\"." },
      { text: "Google opens an \"Add this calendar?\" dialog — confirm with your Google account." },
      { text: "The calendar appears in the left column of calendar.google.com, under \"Other calendars\". Make sure it is checked to display events. ✅", mock: "gweb" },
    ],
    apple: [
      { text: "On iPhone or iPad, open the SHIALOC calendar and tap \"Sync\" → \"Apple Calendar\"." },
      { text: "iOS shows \"Subscribe to calendar?\" — tap \"Subscribe\".", mock: "apple" },
      { text: "Open the Calendar app → \"Calendars\" at the bottom: your association appears under \"Subscriptions\". ✅" },
    ],
    manual: [
      { text: "In the \"Sync\" menu, tap \"Copy feed link\"." },
      { text: "On calendar.google.com (desktop): click the \"+\" next to \"Other calendars\" → \"From URL\" → paste the link → \"Add calendar\".", mock: "manual" },
      { text: "This link also works with Outlook, Thunderbird and any iCal-compatible app. ✅" },
    ],
    mocks: {
      gmobileTitle: "Settings", gmobileSync: "Sync",
      gwebOthers: "Other calendars", gwebAdd: "+",
      appleTitle: "Subscribe to calendar?", appleDesc: "The association's events will be added to your calendar.", appleCancel: "Cancel", appleOk: "Subscribe",
      manualLabel: "From URL", manualBtn: "Add calendar",
      assocName: "Your association",
    },
  },
};

const Mock = ({ type, m }) => {
  if (type === "gmobile") return (
    <div className="rounded-2xl border border-border bg-card w-full max-w-[260px] overflow-hidden shadow-sm" data-testid="mock-gmobile">
      <div className="px-4 py-2.5 border-b border-border text-xs font-semibold">{m.gmobileTitle}</div>
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-medium">{m.assocName}</p>
          <p className="text-[10px] text-muted-foreground">{m.gmobileSync}</p>
        </div>
        <span className="inline-flex h-5 w-9 items-center rounded-full bg-emerald-500 px-0.5 justify-end shrink-0">
          <span className="h-4 w-4 rounded-full bg-white block" />
        </span>
      </div>
    </div>
  );
  if (type === "gweb") return (
    <div className="rounded-xl border border-border bg-card w-full max-w-[260px] overflow-hidden shadow-sm" data-testid="mock-gweb">
      <div className="px-4 py-2.5 flex items-center justify-between border-b border-border">
        <span className="text-xs font-semibold">{m.gwebOthers}</span>
        <span className="h-5 w-5 rounded-full border border-border flex items-center justify-center text-xs text-muted-foreground">{m.gwebAdd}</span>
      </div>
      <div className="px-4 py-2.5 flex items-center gap-2.5">
        <span className="h-3.5 w-3.5 rounded-sm bg-primary flex items-center justify-center text-[9px] text-primary-foreground">✓</span>
        <span className="text-xs">{m.assocName}</span>
      </div>
    </div>
  );
  if (type === "apple") return (
    <div className="rounded-2xl border border-border bg-card w-full max-w-[260px] p-4 text-center shadow-sm" data-testid="mock-apple">
      <p className="text-xs font-semibold mb-1">{m.appleTitle}</p>
      <p className="text-[10px] text-muted-foreground mb-3">{m.appleDesc}</p>
      <div className="grid grid-cols-2 gap-px border-t border-border pt-2 text-xs">
        <span className="text-muted-foreground">{m.appleCancel}</span>
        <span className="text-primary font-semibold">{m.appleOk}</span>
      </div>
    </div>
  );
  return (
    <div className="rounded-xl border border-border bg-card w-full max-w-[300px] p-4 space-y-2.5 shadow-sm" data-testid="mock-manual">
      <p className="text-xs font-semibold">{m.manualLabel}</p>
      <div className="rounded-md border border-border bg-muted/40 px-3 py-1.5 text-[10px] font-mono-data text-muted-foreground truncate">
        https://shialoc.com/api/calendar/feed/…ics
      </div>
      <span className="inline-block rounded-full bg-primary text-primary-foreground text-[10px] px-3 py-1">{m.manualBtn}</span>
    </div>
  );
};

const Steps = ({ steps, mocks }) => (
  <ol className="space-y-6 mt-6">
    {steps.map((s, i) => (
      <li key={i} className="flex gap-4" data-testid={`sync-help-step-${i}`}>
        <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0 font-heading">{i + 1}</span>
        <div className="space-y-3 min-w-0">
          <p className="text-sm leading-relaxed">{s.text}</p>
          {s.mock && <Mock type={s.mock} m={mocks} />}
        </div>
      </li>
    ))}
  </ol>
);

export default function SyncHelp() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.fr;
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => window.history.back()} data-testid="sync-help-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> {c.back}
        </button>
        <div className="flex items-center gap-3 mb-3">
          <span className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <RefreshCw className="h-5 w-5 text-primary" />
          </span>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl" data-testid="sync-help-title">{c.title}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">{c.subtitle}</p>

        <div className="mb-8 flex gap-3 rounded-lg border border-emerald-500/25 bg-emerald-500/5 px-4 py-3.5" data-testid="sync-help-auto-banner">
          <Zap className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold mb-1">{c.autoTitle}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{c.autoText}</p>
          </div>
        </div>

        <Tabs defaultValue="gmobile">
          <TabsList className="flex flex-wrap h-auto gap-1 w-full justify-start">
            <TabsTrigger value="gmobile" data-testid="sync-tab-gmobile" className="text-xs gap-1.5"><Smartphone className="h-3.5 w-3.5" /> {c.tabs.gmobile}</TabsTrigger>
            <TabsTrigger value="gweb" data-testid="sync-tab-gweb" className="text-xs gap-1.5"><Monitor className="h-3.5 w-3.5" /> {c.tabs.gweb}</TabsTrigger>
            <TabsTrigger value="apple" data-testid="sync-tab-apple" className="text-xs gap-1.5"><Apple className="h-3.5 w-3.5" /> {c.tabs.apple}</TabsTrigger>
            <TabsTrigger value="manual" data-testid="sync-tab-manual" className="text-xs gap-1.5"><Link2 className="h-3.5 w-3.5" /> {c.tabs.manual}</TabsTrigger>
          </TabsList>
          <TabsContent value="gmobile"><Steps steps={c.gmobile} mocks={c.mocks} /></TabsContent>
          <TabsContent value="gweb"><Steps steps={c.gweb} mocks={c.mocks} /></TabsContent>
          <TabsContent value="apple"><Steps steps={c.apple} mocks={c.mocks} /></TabsContent>
          <TabsContent value="manual"><Steps steps={c.manual} mocks={c.mocks} /></TabsContent>
        </Tabs>

        <div className="mt-10 flex gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3.5" data-testid="sync-help-note">
          <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">{c.note}</p>
        </div>
      </div>
    </div>
  );
}
